import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ChevronLeft, 
  Edit, 
  CheckCircle, 
  DollarSign, 
  Send, 
  FileDown, 
  Plus,
  Trash,
  Mail,
  Download,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { DataTable, Column } from '@/components/common/DataTable';
import { 
  PayRunItem,
  Payslip,
  PAY_RUN_STATUSES,
} from '@/types';
import { 
  usePayRun, 
  useApprovePayRun, 
  useMarkPaid, 
  useDeletePayRunItem,
  useEmailAllPayslips,
} from '@/hooks/usePayRuns';
import { usePrepareSTPSubmission } from '@/hooks/useSTP';

import { formatCurrency, formatDate } from '@/lib/utils';

const getStatusBadge = (status: string) => {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info'> = {
    draft: 'secondary',
    pending_approval: 'warning',
    approved: 'info',
    paid: 'success',
    cancelled: 'destructive',
  };
  const config = PAY_RUN_STATUSES.find((s) => s.value === status);
  return <Badge variant={variants[status] || 'default'}>{config?.label || status}</Badge>;
};

export function PayRunDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('items');

  const { data: payRun, isLoading, refetch } = usePayRun(id!);
  const approvePayRun = useApprovePayRun();
  const markPaid = useMarkPaid();
  const submitToATO = usePrepareSTPSubmission();
  const deletePayRunItem = useDeletePayRunItem();
  const emailAllPayslips = useEmailAllPayslips();


  const handleApprove = async () => {
    try {
      await approvePayRun.mutateAsync(id!);
      toast({
        title: 'Success',
        description: 'Pay run approved successfully',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to approve pay run',
        variant: 'destructive',
      });
    }
  };

  const handleMarkPaid = async () => {
    try {
      await markPaid.mutateAsync(id!);
      toast({
        title: 'Success',
        description: 'Pay run marked as paid successfully',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to mark pay run as paid',
        variant: 'destructive',
      });
    }
  };

  const handleSubmitSTP = async () => {
    try {
      await submitToATO.mutateAsync(id!);
      toast({
        title: 'Success',
        description: 'STP submission sent to ATO successfully',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to submit STP',
        variant: 'destructive',
      });
    }
  };

  const handleGenerateABA = async () => {
    try {
      // This would typically open a modal to collect bank account details
      toast({
        title: 'Info',
        description: 'ABA generation requires bank account details',
        variant: 'default',
      });
      navigate(`/payroll/aba?payRunId=${id}`);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to generate ABA file',
        variant: 'destructive',
      });
    }
  };

  const handleEmailPayslips = async () => {
    try {
      const result = await emailAllPayslips.mutateAsync(id!);
      toast({
        title: 'Success',
        description: `Sent ${result.sent} payslips (${result.failed} failed)`,
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to email payslips',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!confirm('Are you sure you want to remove this employee from the pay run?')) return;
    
    try {
      await deletePayRunItem.mutateAsync({ payRunId: id!, itemId });
      toast({
        title: 'Success',
        description: 'Employee removed from pay run',
        variant: 'success',
      });
      refetch();
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to remove employee',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!payRun) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Pay run not found</p>
        <Button onClick={() => navigate('/payroll/pay-runs')} className="mt-4">
          Back to Pay Runs
        </Button>
      </div>
    );
  }

  const itemsColumns: Column<PayRunItem>[] = [
    {
      key: 'employee',
      header: 'Employee',
      cell: (row) => (
        <div>
          <p className="font-medium">{row.employeeName}</p>
          <p className="text-sm text-muted-foreground">{row.employeeCode}</p>
        </div>
      ),
    },
    {
      key: 'ordinary',
      header: 'Ordinary',
      cell: (row) => (
        <span>{row.ordinaryHours.toFixed(2)} hrs @ {formatCurrency(row.ordinaryRate)}/hr</span>
      ),
    },
    {
      key: 'overtime',
      header: 'Overtime',
      cell: (row) => row.overtimeHours > 0 ? (
        <span>{row.overtimeHours.toFixed(2)} hrs @ {formatCurrency(row.overtimeRate)}/hr</span>
      ) : (
        <span className="text-muted-foreground">-</span>
      ),
    },
    {
      key: 'allowances',
      header: 'Allowances',
      cell: (row) => formatCurrency(row.totalAllowances),
    },
    {
      key: 'deductions',
      header: 'Deductions',
      cell: (row) => formatCurrency(row.totalDeductions),
    },
    {
      key: 'gross',
      header: 'Gross',
      cell: (row) => <span className="font-medium">{formatCurrency(row.gross)}</span>,
    },
    {
      key: 'tax',
      header: 'Tax',
      cell: (row) => formatCurrency(row.tax),
    },
    {
      key: 'super',
      header: 'Super',
      cell: (row) => formatCurrency(row.superContribution),
    },
    {
      key: 'net',
      header: 'Net Pay',
      cell: (row) => <span className="font-bold">{formatCurrency(row.netPay)}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      cell: (row) => payRun.status === 'draft' ? (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" asChild>
            <Link to={`/payroll/pay-runs/${id}/items/${row.id}`}>
              <Edit className="h-4 w-4" />
            </Link>
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => handleDeleteItem(row.id)}
          >
            <Trash className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ) : null,
    },
  ];

  const payslipsColumns: Column<Payslip>[] = [
    {
      key: 'employee',
      header: 'Employee',
      cell: (row) => <span className="font-medium">{row.item.employeeName}</span>,
    },
    {
      key: 'gross',
      header: 'Gross',
      cell: (row) => formatCurrency(row.item.gross),
    },
    {
      key: 'net',
      header: 'Net Pay',
      cell: (row) => <span className="font-bold">{formatCurrency(row.item.netPay)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <div className="flex gap-1">
          {row.emailedAt && <Badge variant="info" className="text-xs">Emailed</Badge>}
          {row.downloadedAt && <Badge variant="success" className="text-xs">Downloaded</Badge>}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
      cell: (row) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" asChild>
            <Link to={`/payroll/payslips/${row.id}`}>
              <FileDown className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">{payRun.payRunNumber}</h1>
              {getStatusBadge(payRun.status)}
            </div>
            <p className="text-sm text-muted-foreground">
              {formatDate(payRun.periodStartDate)} - {formatDate(payRun.periodEndDate)} • Payment: {formatDate(payRun.paymentDate)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {payRun.status === 'draft' && (
            <>
              <Button variant="outline" asChild>
                <Link to={`/payroll/pay-runs/${id}?edit=true`}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit
                </Link>
              </Button>
              <Button onClick={handleApprove}>
                <CheckCircle className="mr-2 h-4 w-4" />
                Approve
              </Button>
            </>
          )}
          {payRun.status === 'approved' && (
            <>
              <Button onClick={handleMarkPaid}>
                <DollarSign className="mr-2 h-4 w-4" />
                Mark as Paid
              </Button>
              <Button variant="outline" onClick={handleSubmitSTP}>
                <Send className="mr-2 h-4 w-4" />
                Submit STP
              </Button>
            </>
          )}
          {payRun.status === 'paid' && (
            <>
              <Button variant="outline" onClick={handleGenerateABA}>
                <Download className="mr-2 h-4 w-4" />
                Generate ABA
              </Button>
              <Button variant="outline" onClick={handleEmailPayslips}>
                <Mail className="mr-2 h-4 w-4" />
                Email Payslips
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Wages</p>
            <p className="text-2xl font-bold">{formatCurrency(payRun.totalWages)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Tax</p>
            <p className="text-2xl font-bold">{formatCurrency(payRun.totalTax)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Super</p>
            <p className="text-2xl font-bold">{formatCurrency(payRun.totalSuper)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Net Pay</p>
            <p className="text-2xl font-bold text-success">{formatCurrency(payRun.totalNetPay)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 lg:w-auto">
          <TabsTrigger value="items">Pay Items ({payRun.items?.length || 0})</TabsTrigger>
          <TabsTrigger value="payslips">Payslips</TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="space-y-4">
          {payRun.status === 'draft' && (
            <div className="flex justify-end">
              <Button asChild>
                <Link to={`/payroll/pay-runs/${id}/items/new`}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Employee
                </Link>
              </Button>
            </div>
          )}
          <DataTable
            data={payRun.items || []}
            columns={itemsColumns}
            keyExtractor={(row) => row.id}
            emptyMessage="No pay items found. Add employees to the pay run."
          />
        </TabsContent>

        <TabsContent value="payslips">
          <DataTable
            data={payRun.items?.map((item) => ({
              id: `${payRun.id}-${item.employeeId}`,
              payRunId: payRun.id,
              employeeId: item.employeeId,
              payRunNumber: payRun.payRunNumber,
              periodStartDate: payRun.periodStartDate,
              periodEndDate: payRun.periodEndDate,
              paymentDate: payRun.paymentDate,
              generatedAt: payRun.createdAt,
              item: item,
              ytdTotals: { gross: 0, tax: 0, super: 0, net: 0 },
            })) || []}
            columns={payslipsColumns}
            keyExtractor={(row) => row.id}
            emptyMessage="No payslips available"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
